import { comboKey } from "@/domain/profile-resolver/index";
import type { CharBind, Profile } from "@/domain/types";
import { destinyAlternates, parseCvars } from "./parser";

// Destiny 2 stores its key bindings in a real local cvars.xml (retiring the "cloud-only, no
// export file" premise). This is the shared Destiny ingestion (domain.md: ingestion is per-Game),
// the analogue of ingestSC2: parse the file, curate via the DESTINY_BINDS registry, translate each
// token (destinyAlternates), and emit the character-space Profile the one projector renders. BOTH
// Destiny Profiles - the built-in stock Default and the Edennil Source - are file-derived
// through this one path; they differ only by which cvars.xml feeds it (and a pin for the board-bound
// Edennil setup).
//
// On Foot and Vehicle are sibling flat Contexts: Destiny reuses the same physical keys for
// different actions by in-game state (W is the Kinetic weapon on foot, Dodge Left in a vehicle), so
// they cannot share one resolved layer. The interface/menu keys + push-to-talk sit in "base" (shared
// by both Contexts); everything else is mode-specific.

type DestinyCtx = "base" | "on-foot" | "vehicle";

interface DestinyBind {
  /** The cvars.xml `key bindings` cvar name - also the Command id (stable per-action identity). */
  cvar: string;
  name: string;
  category: string;
  /** Which Context tier: "base" = shared by both states, else the flat On-Foot / Vehicle overlay. */
  ctx: DestinyCtx;
  /**
   * Also active in the Vehicle Context, where it takes the cap on a shared key. The movement binds
   * steer a Sparrow too, so they overlay the vehicle view on top of the vehicle-specific binds
   * (prepended, so the projector's first-wins gives movement the key - the player's "movement
   * priority" ask). The displaced vehicle action is auto-recorded as a shadow on the movement key
   * (overlaySteering).
   */
  inVehicle?: boolean;
  /**
   * A profile-AGNOSTIC note, true of any setup binding this cvar (e.g. "Hold to zoom" on Aim). A note
   * that describes a data-specific overload (a key one setup binds to two actions) is NOT put here -
   * it would be false for another setup - it comes from the ingest's `annotate` option instead.
   */
  note?: string;
}

interface IngestOptions {
  id: string;
  name: string;
  /** A board-bound setup's pinned lens; absent = board-portable (the viewer's own lens). */
  pin?: Profile["pin"];
}

// The curation registry: cvar -> Command (display name / Category / Context). Shared by every Destiny
// Profile. Deliberately omitted, each a curation call (not a parser gap):
//  - light_attack / heavy_attack: sword-mode aliases of fire / aim - they live on LMB / RMB, where
//    the card already shows Fire / Aim (one command per key), so a separate entry would
//    only fight them for the same button. Block does NOT belong here: it is a distinct action on its
//    own key (see below).
//  - vehicle_zoom: shares the right mouse button with vehicle_boost (folded into Boost's note).
//  - death_cam_zoom_in / out, and the rest of the ui_* / system layer (chat, roster, screenshot, ...).
// Block and Tactical Ability (equipment_ability) are KEPT: they are distinct in-game actions (each its
// own labelled row in Settings and its own stock default key - block=c, equipment_ability=z), not
// duplicates of melee / air_move. Block coincides with melee only in Bungie's stock default (both on
// c), where the Default profile skips it and notes it on Melee (destiny-2.ts); a setup that gives it
// its own key (Edennil: backslash) shows it outright.
const DESTINY_BINDS: DestinyBind[] = [
  // base - shared by On Foot and Vehicle (the interface / menu keys + push-to-talk)
  { cvar: "ui_gamepad_button_back", name: "Ghost", category: "interface", ctx: "base" },
  {
    cvar: "ui_abort_activity",
    name: "Return to Orbit",
    category: "interface",
    ctx: "base",
    note: "Abort activity",
  },
  { cvar: "ui_open_director", name: "Open Director", category: "interface", ctx: "base" },
  { cvar: "ui_open_start_menu_alternative", name: "Open Menu", category: "interface", ctx: "base" },
  { cvar: "push_to_talk", name: "Push to Talk", category: "utility", ctx: "base" },

  // on-foot - movement. The four directions + Jump also steer a vehicle (inVehicle), so they overlay
  // the Vehicle Context with priority; Sprint/Crouch have no Sparrow analog and stay on-foot only.
  { cvar: "move_forward", name: "Forward", category: "movement", ctx: "on-foot", inVehicle: true },
  { cvar: "move_left", name: "Left", category: "movement", ctx: "on-foot", inVehicle: true },
  { cvar: "move_backward", name: "Back", category: "movement", ctx: "on-foot", inVehicle: true },
  { cvar: "move_right", name: "Right", category: "movement", ctx: "on-foot", inVehicle: true },
  { cvar: "jump", name: "Jump", category: "movement", ctx: "on-foot", inVehicle: true },
  // crouch/sprint are bound by either the hold_ or toggle_ cvar (a setup uses one; the vendored
  // exports carry the unused counterpart as the empty filler); both variants name the one action
  { cvar: "hold_crouch", name: "Crouch", category: "movement", ctx: "on-foot", note: "Hold" },
  { cvar: "toggle_crouch", name: "Crouch", category: "movement", ctx: "on-foot", note: "Toggle" },
  { cvar: "hold_sprint", name: "Sprint", category: "movement", ctx: "on-foot", note: "Hold" },
  { cvar: "toggle_sprint", name: "Sprint", category: "movement", ctx: "on-foot", note: "Toggle" },

  // on-foot - weapon
  { cvar: "primary_weapon", name: "Kinetic", category: "weapon", ctx: "on-foot" },
  { cvar: "special_weapon", name: "Energy", category: "weapon", ctx: "on-foot" },
  { cvar: "heavy_weapon", name: "Power", category: "weapon", ctx: "on-foot" },
  { cvar: "fire", name: "Fire", category: "weapon", ctx: "on-foot" },
  // zoom is a hold/toggle pair too, like crouch/sprint above
  { cvar: "hold_zoom", name: "Aim", category: "weapon", ctx: "on-foot", note: "Hold to zoom" },
  { cvar: "toggle_zoom", name: "Aim", category: "weapon", ctx: "on-foot", note: "Toggle zoom" },
  { cvar: "reload", name: "Reload", category: "weapon", ctx: "on-foot" },
  { cvar: "switch_weapons", name: "Switch Weapon", category: "weapon", ctx: "on-foot" },
  { cvar: "next_weapon", name: "Next Weapon", category: "weapon", ctx: "on-foot" },
  { cvar: "previous_weapon", name: "Previous Weapon", category: "weapon", ctx: "on-foot" },

  // on-foot - ability
  { cvar: "grenade", name: "Grenade", category: "ability", ctx: "on-foot" },
  { cvar: "melee", name: "Melee", category: "ability", ctx: "on-foot" },
  // Block (sword guard) is its own action; in Bungie's stock layout it coincides with Melee on c, so it
  // is listed AFTER Melee - first-wins keeps Melee primary and records Block as its shadow (decision
  // 0034). A setup that gives Block its own key (Edennil: backslash) shows it on its own cap.
  { cvar: "block", name: "Block", category: "weapon", ctx: "on-foot" },
  { cvar: "melee_uncharged", name: "Uncharged Melee", category: "ability", ctx: "on-foot" },
  { cvar: "melee_charged", name: "Charged Melee", category: "ability", ctx: "on-foot" },
  { cvar: "super", name: "Super", category: "ability", ctx: "on-foot" },
  {
    cvar: "class_ability",
    name: "Class",
    category: "ability",
    ctx: "on-foot",
    note: "Barricade / Rift / Dodge",
  },
  {
    cvar: "stasis_breakout",
    name: "Breakout",
    category: "ability",
    ctx: "on-foot",
    note: "Stasis breakout",
  },
  { cvar: "utility_ability", name: "Transcendence", category: "ability", ctx: "on-foot" },
  { cvar: "air_move", name: "Air Move", category: "ability", ctx: "on-foot" },
  { cvar: "equipment_ability", name: "Tactical Ability", category: "ability", ctx: "on-foot" },
  // in-game label is "Finisher / Player Highlight"; the cap shows the primary action, the rest the note
  {
    cvar: "highlight_player",
    name: "Finisher",
    category: "ability",
    ctx: "on-foot",
    note: "Also Player Highlight (ping)",
  },

  // on-foot - utility
  { cvar: "interact", name: "Interact", category: "utility", ctx: "on-foot" },
  { cvar: "emote_1", name: "Emote 1", category: "utility", ctx: "on-foot" },
  { cvar: "emote_2", name: "Emote 2", category: "utility", ctx: "on-foot" },
  { cvar: "emote_3", name: "Emote 3", category: "utility", ctx: "on-foot" },
  { cvar: "emote_4", name: "Emote 4", category: "utility", ctx: "on-foot" },

  // vehicle - the same physical keys, a different in-game state (a separate Context)
  { cvar: "vehicle_fire_primary", name: "Primary Fire", category: "vehicle", ctx: "vehicle" },
  { cvar: "vehicle_fire_secondary", name: "Secondary Fire", category: "vehicle", ctx: "vehicle" },
  {
    cvar: "vehicle_boost",
    name: "Boost",
    category: "vehicle",
    ctx: "vehicle",
    note: "Shares RMB with Vehicle Zoom",
  },
  { cvar: "vehicle_brake", name: "Brake", category: "vehicle", ctx: "vehicle" },
  { cvar: "vehicle_jump", name: "Vehicle Jump", category: "vehicle", ctx: "vehicle" },
  { cvar: "vehicle_dodge_left", name: "Dodge Left", category: "vehicle", ctx: "vehicle" },
  { cvar: "vehicle_dodge_right", name: "Dodge Right", category: "vehicle", ctx: "vehicle" },
  { cvar: "vehicle_biped_dodge", name: "Heavy Dodge", category: "vehicle", ctx: "vehicle" },
  { cvar: "vehicle_exit", name: "Exit Vehicle", category: "vehicle", ctx: "vehicle" },
  { cvar: "vehicle_honk", name: "Vehicle Horn", category: "vehicle", ctx: "vehicle" },
  { cvar: "emote_1", name: "Vehicle Trick 1", category: "vehicle", ctx: "vehicle" },
  { cvar: "emote_2", name: "Vehicle Trick 2", category: "vehicle", ctx: "vehicle" },
  { cvar: "emote_3", name: "Vehicle Trick 3", category: "vehicle", ctx: "vehicle" },
  { cvar: "emote_4", name: "Vehicle Trick 4", category: "vehicle", ctx: "vehicle" },
];

/**
 * Overlay the movement (steering) binds onto the vehicle tier so they also steer a Sparrow, taking the
 * cap on a shared key (movement priority). Prepended, so the projector's first-wins (decision 0034)
 * gives movement the key and auto-records the displaced vehicle action as a shadow on the survivor - no
 * hand-authored conflict note needed; the universal mechanism surfaces it.
 */
function overlaySteering(vehicle: CharBind[], steering: CharBind[]): void {
  vehicle.unshift(...steering);
}

/**
 * Ingest a Destiny 2 cvars.xml into a character-space Profile. `base` binds are shared by
 * both Contexts; "on-foot" and "vehicle" become the two flat-Context overlays. The directional
 * movement binds (inVehicle) also overlay the vehicle view, with priority (overlaySteering). A key one
 * setup binds to two actions surfaces through the universal first-wins + shadowed mechanism (decision
 * 0034) - no per-setup skip/annotate. `pin` makes it board-bound.
 */
export function ingestDestiny(xml: string, opts: IngestOptions): Profile {
  const cvars = parseCvars(xml);
  const tiers: Record<DestinyCtx, CharBind[]> = { base: [], "on-foot": [], vehicle: [] };
  const steering: CharBind[] = [];
  for (const b of DESTINY_BINDS) {
    const value = cvars[b.cvar];
    if (value == null) continue;
    const binding = { name: b.name, category: b.category, cmd: b.cvar } as CharBind["binding"];
    if (b.note) binding.note = b.note;
    for (const alt of destinyAlternates(value)) {
      const cb: CharBind = { binding: { ...binding }, token: alt.token, combo: comboKey(alt.mods) };
      tiers[b.ctx].push(cb);
      if (b.inVehicle) steering.push({ binding: { ...binding }, token: cb.token, combo: cb.combo });
    }
  }
  overlaySteering(tiers.vehicle, steering);
  const profile: Profile = {
    id: opts.id,
    name: opts.name,
    base: [tiers.base],
    contexts: { "on-foot": tiers["on-foot"], vehicle: tiers.vehicle },
  };
  if (opts.pin) profile.pin = opts.pin;
  return profile;
}
